import { useFormaliteService, useOffreService } from '@europrocurement/l2d-domain';
import {
    PrestationArticlesJsonldPrestationrelationRead,
    TblFactArticlesJsonldPrestationrelationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';

export type DossierArticleCategory = {
    label: string;
};

export type DossierArticle = {
    id: number;
    label: string;
    categoryId: number | undefined;
    dossierId: number;
    article: TblFactArticlesJsonldPrestationrelationRead;
    prestationId: string;
    idStype?: number;
    rang: number;
};

export type DossierArticleWithInvoice = {
    id: number;
    label: string;
    categoryId: number | undefined;
    dossierId: number;
    article: TblFactArticlesJsonldPrestationrelationRead;
    prestationId: string;
    idStype?: number;
    rang: number;
    hasInvoices: boolean;
    invoiceNumber: string | undefined;
};

export type AddArticleParamsType = {
    prestationId: number;
    typeArticleId: number | undefined;
    label: string;
    factArticleId: number;
};

export type UpdateArticleParamsType = {
    prestaArticleId: number;
    prestationId: number;
    typeArticleId: number | undefined;
    label: string;
    factArticleId: number;
};

const useDossierArticle = () => {
    const { prestationArticlesApi } = useFormaliteService();
    const { articlesApi } = useOffreService();

    const getArticles = async (
        dossierId: number,
        categoryId?: number,
    ): Promise<Array<DossierArticle>> => {
        const response = await prestationArticlesApi.apiArticlesPrestationGetCollection({
            idPrestaDossierId: dossierId,
            factArticleCategoryId: categoryId,
            fMasqueCalcul: false,
        });

        return response.data['hydra:member'].map((item) => ({
            id: item.id as number,
            label: item.libelle as string,
            categoryId,
            dossierId,
            article: item.factArticle,
            prestationId: item.idPresta,
            idStype: item.idstype?.id,
            rang: item.rang as number,
        }));
    };

    const getArticlesWithInvoices = async (
        dossierId: number,
        categoryId?: number,
    ): Promise<Array<DossierArticleWithInvoice>> => {
        const response = await prestationArticlesApi.apiArticlesPrestationFactureGetCollection({
            idPrestaDossierId: dossierId,
            factArticleCategoryId: categoryId,
            fMasqueCalcul: false,
        });

        return response.data['hydra:member'].map((item) => ({
            id: item.id as number,
            label: item.libelle as string,
            categoryId,
            dossierId,
            article: item.factArticle,
            prestationId: item.idPresta,
            idStype: item.idstype?.id,
            rang: item.rang as number,
            hasInvoices: (item.flignes && item.flignes.length > 0) || false,
            invoiceNumber:
                (item.flignes && item.flignes.length > 0 && item.flignes[0].facture?.numero) ||
                undefined,
        }));
    };

    const getArticlesWithInvoicesByDossier = async (
        dossierId: number,
    ): Promise<Array<DossierArticleWithInvoice>> => {
        const response = await prestationArticlesApi.apiArticlesPrestationFactureGetCollection({
            idPrestaDossierId: dossierId,
            fMasqueCalcul: false,
            itemsPerPage: 999,
        });

        return response.data['hydra:member'].map((item) => ({
            id: item.id as number,
            label: item.libelle as string,
            categoryId: item.factArticle.category?.id as number,
            dossierId,
            article: item.factArticle,
            prestationId: item.idPresta,
            idStype: item.idstype?.id,
            rang: item.rang as number,
            hasInvoices: (item.flignes && item.flignes.length > 0) || false,
            invoiceNumber:
                (item.flignes && item.flignes.length > 0 && item.flignes[0].facture?.numero) ||
                undefined,
        }));
    };

    const addArticle = async ({
        prestationId,
        typeArticleId,
        label,
        factArticleId,
    }: AddArticleParamsType): Promise<number> => {
        // Fetch the article to extract article price
        const factArticle = await articlesApi.apiArticlesIdGet({ id: factArticleId.toString() });

        const result = await prestationArticlesApi.apiArticlesPrestationPost({
            prestationArticlesJsonldPrestationrelationCreate: {
                idPresta: prestationId as unknown as string,
                factArticle: factArticleId as unknown as string,
                libelle: label,
                quantite: 1,
                tarif: factArticle?.data?.prixUnitaire
                    ? factArticle.data.prixUnitaire.toString()
                    : '0.0000',
                idstype: typeArticleId ? (typeArticleId as unknown as string) : undefined,
            },
        });
        return result.data.id as number;
    };

    const updateArticle = async ({
        prestaArticleId,
        prestationId,
        typeArticleId,
        label,
        factArticleId,
    }: UpdateArticleParamsType): Promise<PrestationArticlesJsonldPrestationrelationRead> => {
        const result = await prestationArticlesApi.apiArticlesPrestationIdPut({
            prestationArticlesJsonldPrestationrelationUpdate: {
                idPresta: `/formalite/prestations/${prestationId}`,
                factArticle: factArticleId as unknown as string,
                libelle: label,
                quantite: 1,
                idstype: typeArticleId ? (typeArticleId as unknown as string) : undefined,
            },
            id: prestaArticleId.toString(),
        });
        return result.data;
    };

    const deleteArticle = async (prestaArticleId: string): Promise<void> => {
        const result = await prestationArticlesApi.apiArticlesPrestationIdDelete({
            id: prestaArticleId,
        });
        return result.data;
    };

    return {
        getArticles,
        getArticlesWithInvoices,
        getArticlesWithInvoicesByDossier,
        addArticle,
        deleteArticle,
        updateArticle,
    };
};

export default useDossierArticle;
